import { C } from '../lib/theme';
import { notificationContactLabel } from '../state/actions/lifecycle';
import { styles } from '../lib/styles';
import { fmtDate, daysBetween } from '../lib/util';
import { DeadlineIndicator } from '../components/DeadlineIndicator';
import { PagingControls } from '../components/PagingControls';
import { SearchFilterBar, useSearchFilter } from '../components/SearchFilterBar';
import { useAppData } from '../state/contexts/AppDataContext';
import { useLifecycleActions } from '../state/actions/lifecycle';
import { unionMatching, useCaseQuery } from '../state/useCaseQuery';

export function ReVets() {
  const { cases, now } = useAppData();
  const { completeReVet, initiateReVet } = useLifecycleActions();

  // H-2: annual re-vets are by definition on year-old cases — exactly what
  // fell out of the old most-recent-50 window. Both sections query the full
  // population server-side; the store union keeps local transitions
  // (initiateReVet / completeReVet) visible without a refetch.
  const scheduledQuery = useCaseQuery({ revetStatus: 'SCHEDULED', sort: 'revetDueAt', order: 'asc' });
  const requestedQuery = useCaseQuery({ revetStatus: 'REQUESTED' });
  const awaitingQuery = useCaseQuery({ revetStatus: 'AWAITING_RESPONSE' });

  const scheduled = unionMatching(scheduledQuery.cases, cases, c => c.revetStatus === 'SCHEDULED', scheduledQuery.offset)
    .sort((a, b) => new Date(a.revetDueAt!).getTime() - new Date(b.revetDueAt!).getTime());
  const requested = unionMatching(
    [...requestedQuery.cases, ...awaitingQuery.cases], cases,
    c => c.revetStatus === 'REQUESTED' || c.revetStatus === 'AWAITING_RESPONSE',
  );

  // Client-side v1 search over the in-memory rows the per-section server
  // queries already returned (server-side filtering is a deferred phase 2).
  // The due-window facet only applies to scheduled rows (the ones with a
  // revetDueAt); in-progress rows have no due date so they match every window.
  const dueBucket = (dueAt?: string | null) => {
    if (!dueAt) return null;
    const d = daysBetween(now, dueAt);
    if (d < 0) return 'overdue';
    if (d <= 7) return 'week';
    if (d <= 30) return 'month';
    return 'later';
  };
  const rows = [
    ...requested.map(c => ({ c, section: 'requested' as const })),
    ...scheduled.map(c => ({ c, section: 'scheduled' as const })),
  ];
  const { filtered: filteredRows, bar } = useSearchFilter(
    rows,
    // #693 — the authorizer's name in place of the retired oaName.
    ({ c }) => [c.id, c.registrant, c.eid, c.onboarding?.data?.authorizerName].filter(Boolean).join(' '),
    [
      {
        id: 'due',
        label: 'Due window',
        options: [
          { value: '', label: 'All due windows' },
          { value: 'overdue', label: 'Overdue' },
          { value: 'week', label: 'Due within 7 days' },
          { value: 'month', label: 'Due within 30 days' },
          { value: 'later', label: 'Due later' },
        ],
        // In-progress rows (no revetDueAt) are exempt so the facet doesn't
        // silently hide active work.
        predicate: ({ c, section }, v) => section !== 'scheduled' || dueBucket(c.revetDueAt) === v,
      },
    ],
    { placeholder: 'Search re-vets by case, registrant, EID, or authorizer…' },
  );
  const filteredRequested = filteredRows.filter(r => r.section === 'requested').map(r => r.c);
  const filteredScheduled = filteredRows.filter(r => r.section === 'scheduled').map(r => r.c);

  return (
    <div>
      <h2 style={{ fontSize: 20, fontWeight: 700, color: C.text, marginBottom: 4 }}>Re-Vet Schedule</h2>
      <div style={{ color: C.textMuted, fontSize: 13, marginBottom: 20 }}>Annual re-vets required · 10 calendar day response window for registrants</div>
      {(requested.length > 0 || scheduled.length > 0) && <SearchFilterBar {...bar} />}
      {requested.length > 0 && (
        <div style={{ ...styles.card, borderLeft: `3px solid ${C.warn}` }}>
          <h3 style={{ fontSize: 14, fontWeight: 600, color: C.warn, marginBottom: 12 }}>In Progress ({Math.max(requestedQuery.total + awaitingQuery.total, requested.length)})</h3>
          {filteredRequested.map(c => (
            <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '10px 0', borderBottom: `1px solid ${C.border}` }}>
              <div>
                <strong style={{ color: C.goldText }}>{c.id}</strong> — <strong>{c.registrant}</strong>
                <div style={{ fontSize: 12, color: C.textMuted, marginTop: 4 }}>Request sent: {fmtDate(c.revetRequestedAt)} · Authorizer: {notificationContactLabel(c)}</div>
                <DeadlineIndicator deadline={c.revetResponseDeadline} label="10-day response deadline" now={now} />
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button style={{ ...styles.btn, ...styles.btnSuccess, ...styles.btnSmall }} onClick={() => completeReVet(c.id, false)}>Complete (No Changes)</button>
                <button style={{ ...styles.btn, ...styles.btnGold, ...styles.btnSmall }} onClick={() => completeReVet(c.id, true)}>Complete (Changes Found)</button>
              </div>
            </div>
          ))}
          {filteredRequested.length === 0 && (
            <div style={{ fontSize: 13, color: C.textMuted }}>No in-progress re-vets match the current filters</div>
          )}
        </div>
      )}
      <div style={styles.card}>
        <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Upcoming Re-Vets ({Math.max(scheduledQuery.total, scheduled.length)})</h3>
        <table style={styles.table}>
          <thead><tr>
            <th style={styles.th}>Case</th><th style={styles.th}>Registrant</th><th style={styles.th}>EID</th><th style={styles.th}>Re-Vet Due</th><th style={styles.th}>Days</th><th style={styles.th}>Action</th>
          </tr></thead>
          <tbody>
            {filteredScheduled.map(c => {
              const daysLeft = daysBetween(now, c.revetDueAt!);
              return (
                <tr key={c.id}>
                  <td style={styles.td}><span style={{ color: C.goldText, fontWeight: 600 }}>{c.id}</span></td>
                  <td style={styles.td}>{c.registrant}</td>
                  <td style={styles.td}><span style={{ fontSize: 11, fontFamily: 'monospace' }}>{c.eid}</span></td>
                  <td style={styles.td}>{fmtDate(c.revetDueAt)}</td>
                  <td style={styles.td}><span style={{ color: daysLeft <= 7 ? C.danger : daysLeft <= 30 ? C.warn : C.success, fontWeight: 600 }}>{daysLeft}d</span></td>
                  <td style={styles.td}>{daysLeft <= 60 && <button style={{ ...styles.btn, ...styles.btnGold, ...styles.btnSmall }} onClick={() => initiateReVet(c.id)}>Initiate</button>}</td>
                </tr>
              );
            })}
            {filteredScheduled.length === 0 && <tr><td colSpan={6} style={{ ...styles.td, color: C.textMuted, textAlign: 'center' }}>{scheduled.length === 0 ? 'No scheduled re-vets' : 'No scheduled re-vets match the current filters'}</td></tr>}
          </tbody>
        </table>
        <PagingControls offset={scheduledQuery.offset} limit={scheduledQuery.limit} total={scheduledQuery.total} count={scheduled.length} onOffset={scheduledQuery.setOffset} label="re-vets" />
      </div>
    </div>
  );
}
